import { MutableRefObject, useEffect, useRef, useState } from 'react';

// internal deps
import { useObservable } from 'thrive-observable-react';

// services
import { ConversationService } from '../service/ChatConversation.Service';
import { ChatMessage } from '../components/conversation/ChatMessage.service';

// components
import ChatMessageBubble from '../components/conversation/ChatMessageBubble';
import ChatComposeArea from '../components/conversation/ChatComposeArea';
import ContactInformation from '../components/newChat/ContactInformation';

// interfaces
import { IChatMessage  } from '../interfaces';

// styles
import '../styles/ChatContainer.scss';



function ChatConversation() {
    const chatMessages = useObservable<IChatMessage[]>(ConversationService.chatMessages)
    const chatId = useObservable<number>(ConversationService.chatId);
    const chatIsLoading = useObservable<boolean>(ConversationService.newChatIsLoading);
    const [isAtTop, setIsAtTop] = useState<boolean>(false); 
    const [chatHasChanged, setChatHasChanged] = useState<boolean>(true);

    const isAtBottomRef = useRef<boolean>(true);
    const isAtTopRef = useRef<boolean>(false);
    const prePaginationPositionRef = useRef<{top: number, height: number}>({top: 0, height: 0});
    

    const chatWrapperRef = useRef<HTMLElement>();


    /* This is all purely presentational logic, and would need a different implementation were we to move to another so keeping it in react*/
    useEffect(() => {
        if (!chatWrapperRef.current) return;

        // observer the top of the chat feed, set scroll top to previous top value what chatfeed expands through
        // pagination
        const topObserver = new ResizeObserver(() => {
            if(isAtTopRef.current) {
                 const newScrollHeight = chatWrapperRef?.current?.scrollHeight as number;
                ;(chatWrapperRef.current as HTMLDivElement).scrollTop = prePaginationPositionRef.current.top + (newScrollHeight - prePaginationPositionRef.current.height);
                isAtTopRef.current = false;
            }
        });
        
        const chatFeedElement = document.querySelector('.chat-feed');
        if (chatFeedElement) {
            topObserver.observe(chatFeedElement);
        }

        if(chatHasChanged) {
            setChatHasChanged(false);
            chatWrapperRef.current.scrollTop = chatWrapperRef?.current?.scrollHeight + 1000;
        }

        // if we're at the bottom when we get new messages scroll to the bottom
        else if(isAtBottomRef.current) {
            chatWrapperRef?.current?.scrollTo?.({ top: chatWrapperRef?.current?.scrollHeight + 1000, behavior: 'smooth' });
        }

        // turn off is at top when we get a new page
        else if(isAtTop) {
            setIsAtTop(false);
        }

        return () => {
            topObserver.disconnect(); 
        }
    }, [chatMessages.length])

    // when is at top is set in state we need to store the current position of the scroll before requesting the next page
    useEffect(() => {
        if(isAtTop && chatMessages.length) {
            prePaginationPositionRef.current = {
                top: chatWrapperRef?.current?.scrollTop as number,
                height: chatWrapperRef?.current?.scrollHeight as number
            }
            ConversationService.getNextPage(isAtTop);
            
        }
    },[isAtTop])


    useEffect(() => {
        // observe the bottom of the div, resize and scroll when textbox and wrapper size changes
        const bottomObserver = new ResizeObserver(() => {
            if(isAtBottomRef.current) {
                chatWrapperRef?.current?.scrollTo?.({ top: chatWrapperRef?.current?.scrollHeight + 1000, behavior: 'smooth' });
                isAtBottomRef.current = true
            }
        });
        
        bottomObserver.observe(chatWrapperRef.current as HTMLElement);

        function onScroll() {
            const chatFeedWrapper = document.querySelector('.chat-feed-wrapper') as HTMLDivElement;
            const isAtBottom = Math.ceil(chatFeedWrapper.scrollTop + chatFeedWrapper.clientHeight + 150) >= chatFeedWrapper.scrollHeight
            isAtBottomRef.current = isAtBottom;
            
            const isAtTop = chatFeedWrapper.scrollTop <= 5;
            isAtTopRef.current = isAtTop;

            // debouce setting scrolltop for first load of page
            if(isAtTop) {
                setTimeout(() => {
                    setIsAtTop(isAtTopRef.current);
                }, 500)
            }
        };
        
        document?.querySelector?.('.chat-feed-wrapper')?.addEventListener('scroll', onScroll)
        
        return () => {
            ConversationService.endConversation();
            bottomObserver.disconnect(); 
            document?.querySelector?.('.chat-feed-wrapper')?.removeEventListener('scroll', onScroll);
        }
    },[]);

    useEffect(() => {
        setChatHasChanged(true);
        setIsAtTop(false);
        prePaginationPositionRef.current = {top: 0, height: 0};
    }, [chatId])
   
   
    return (
        <section id='chat-conversation-container'>
            <ChatComposeArea/>

            <div className='chat-feed-wrapper' ref={chatWrapperRef as MutableRefObject<HTMLDivElement>}>
                {!chatIsLoading && chatMessages.length > 0 && 
                    <ul className='chat-feed'>
                        {chatMessages.map((message: any, idx: number) => 
                            <ChatMessageBubble 
                                key={message.id} 
                                message={new ChatMessage(message, chatMessages[idx + 1])} 
                            />
                        )}
                    </ul>
                }

                {chatMessages.length === 0 && ConversationService.newChatUserDetails.value &&
                    <ContactInformation 
                        profileImageUrl={ConversationService.newChatUserDetails.value.profilePicture.uri} 
                        displayName={ConversationService.newChatUserDetails.value.displayName} 
                        jobRole={ConversationService.newChatUserDetails.value.jobRole}
                        department={ConversationService.newChatUserDetails.value.department}
                    />
                }
            </div>
        </section>
    );
};

export default ChatConversation;
